/**
 * The vault layout, defined in exactly one place.
 *
 * Every other module asks this file where things live, so changing the
 * convention is a one-file edit rather than a grep-and-pray.
 */

export const VAULT_DIRS = {
  dashboards: 'dashboards',
  widgets: 'widgets',
  sources: 'sources',
  data: 'data',
  todos: 'todos',
  goals: 'goals',
  notes: 'notes',
  themes: 'themes',
  assets: 'assets',
} as const;

export const DEFAULT_DASHBOARD = 'main';

export const dashboardPath = (name: string): string => `${VAULT_DIRS.dashboards}/${name}.md`;
export const widgetPath = (id: string): string => `${VAULT_DIRS.widgets}/${id}.md`;
export const sourcePath = (id: string): string => `${VAULT_DIRS.sources}/${id}.md`;
export const themePath = (name: string): string => `${VAULT_DIRS.themes}/${name}.css`;
export const goalPath = (slug: string): string => `${VAULT_DIRS.goals}/${slug}.md`;

/** Snapshot of a source on a given day — the time-series that powers deltas and sparklines. */
export const dataSnapshotPath = (sourceId: string, isoDate: string): string =>
  `${VAULT_DIRS.data}/${sourceId}/${isoDate}.json`;

/** Most recent fetch for a source, always overwritten. */
export const dataLatestPath = (sourceId: string): string =>
  `${VAULT_DIRS.data}/${sourceId}/latest.json`;

/** Precomputed whole-vault aggregates, built by the indexer workflow. */
export const indexPath = (name: 'recent' | 'tags'): string =>
  `${VAULT_DIRS.data}/index/${name}.json`;

/** `todos/2026-07-27.md` */
export const dailyTodoPath = (isoDate: string): string => `${VAULT_DIRS.todos}/${isoDate}.md`;

/** `dashboards/main.md` → `main`. Returns null for paths outside the dashboards dir. */
export function dashboardNameFromPath(path: string): string | null {
  const prefix = `${VAULT_DIRS.dashboards}/`;
  if (!path.startsWith(prefix) || !path.endsWith('.md')) return null;
  return path.slice(prefix.length, -'.md'.length);
}

/**
 * `widgets/clock.md` → `clock`. Returns null for anything else, including files
 * nested a level deeper — widget ids are flat, so `widgets/a/b.md` is not one.
 */
export function widgetIdFromPath(path: string): string | null {
  const prefix = `${VAULT_DIRS.widgets}/`;
  if (!path.startsWith(prefix) || !path.endsWith('.md')) return null;
  const id = path.slice(prefix.length, -'.md'.length);
  return id.includes('/') ? null : id;
}

/** Local calendar date as `YYYY-MM-DD` in a given IANA zone (never UTC-shifted). */
export function isoDateIn(at: Date, timeZone?: string): string {
  const fmt = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  return fmt.format(at);
}
